<template>
  <div ref="canvas" class="certificate-mapping-canvas" :style="{ aspectRatio: String(aspectRatio) }" @mousedown.self="selectField('')">
    <img v-if="backgroundUrl" :src="backgroundUrl" class="canvas-background" alt="Certificate template">
    <div
      v-for="mapping in mappings"
      :key="mappingId(mapping)"
      class="canvas-field"
      :class="{ selected: mappingId(mapping) === selectedKey, image: mapping.field_kind === 'image' }"
      :style="mappingStyle(mapping)"
      @mousedown.stop.prevent="startInteraction($event, mapping, 'move')"
      @click.stop="selectField(mappingId(mapping))"
    >
      <img v-if="mapping.field_kind === 'image' && assetUrl(mapping)" :src="assetUrl(mapping)" class="mapped-image" :alt="fieldLabel(mapping.field_key)">
      <span v-else>{{ displayValue(mapping) }}</span>
      <button
        v-if="editable && mappingId(mapping) === selectedKey"
        type="button"
        class="resize-handle"
        aria-label="Resize field"
        @mousedown.stop.prevent="startInteraction($event, mapping, 'resize')"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CertificateMappingCanvas',
  props: {
    backgroundUrl: { type: String, default: '' },
    mappings: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    values: { type: Object, default: () => ({}) },
    assetUrls: { type: Object, default: () => ({}) },
    aspectRatio: { type: Number, default: 1.414 },
    selectedKey: { type: String, default: '' },
    editable: { type: Boolean, default: true }
  },
  data () { return { interaction: null } },
  beforeDestroy () { this.stopInteraction() },
  methods: {
    mappingId (mapping) { return mapping.mapping_key || mapping.field_key },
    field (key) { return this.fields.find(field => field.key === key || field.field_key === key) },
    fieldLabel (key) { const field = this.field(key); return field ? field.label : key },
    displayValue (mapping) {
      const value = this.values[mapping.field_key]
      if (value !== undefined && value !== null && value !== '') return value
      return this.fieldLabel(mapping.field_key)
    },
    assetUrl (mapping) { return this.assetUrls[String(this.values[mapping.field_key] || '')] || '' },
    mappingStyle (mapping) {
      return {
        left: `${Number(mapping.x || 0) * 100}%`,
        top: `${Number(mapping.y || 0) * 100}%`,
        width: `${Number(mapping.width || 0.01) * 100}%`,
        height: `${Number(mapping.height || 0.01) * 100}%`,
        color: mapping.text_color || '#000000',
        fontWeight: mapping.is_bold ? 'bold' : 'normal',
        fontSize: `${Math.max(8, Number(mapping.font_size || 12) * 0.85)}px`,
        textAlign: mapping.text_align === 'L' ? 'left' : mapping.text_align === 'R' ? 'right' : 'center'
      }
    },
    selectField (key) { this.$emit('select', key) },
    startInteraction (event, mapping, mode) {
      this.selectField(this.mappingId(mapping))
      if (!this.editable || !this.$refs.canvas) return
      const rect = this.$refs.canvas.getBoundingClientRect()
      this.interaction = {
        mappingKey: this.mappingId(mapping),
        mode,
        startX: event.clientX,
        startY: event.clientY,
        rect,
        original: { x: Number(mapping.x), y: Number(mapping.y), width: Number(mapping.width), height: Number(mapping.height) }
      }
      window.addEventListener('mousemove', this.moveInteraction)
      window.addEventListener('mouseup', this.stopInteraction)
    },
    moveInteraction (event) {
      if (!this.interaction) return
      const item = this.interaction
      const dx = (event.clientX - item.startX) / item.rect.width
      const dy = (event.clientY - item.startY) / item.rect.height
      const changes = {}
      if (item.mode === 'move') {
        changes.x = this.clamp(item.original.x + dx, 0, 1 - item.original.width)
        changes.y = this.clamp(item.original.y + dy, 0, 1 - item.original.height)
      } else {
        changes.width = this.clamp(item.original.width + dx, 0.01, 1 - item.original.x)
        changes.height = this.clamp(item.original.height + dy, 0.01, 1 - item.original.y)
      }
      this.$emit('change', { mappingKey: item.mappingKey, changes })
    },
    stopInteraction () {
      window.removeEventListener('mousemove', this.moveInteraction)
      window.removeEventListener('mouseup', this.stopInteraction)
      this.interaction = null
    },
    clamp (value, minimum, maximum) { return Math.round(Math.max(minimum, Math.min(maximum, value)) * 10000) / 10000 }
  }
}
</script>

<style scoped>
.certificate-mapping-canvas { position: relative; width: 100%; overflow: hidden; background: #e9ecef; border: 1px solid #c8ced3; user-select: none; }
.canvas-background { position: absolute; inset: 0; width: 100%; height: 100%; }
.canvas-field { position: absolute; z-index: 2; display: flex; align-items: center; overflow: hidden; cursor: move; border: 1px dashed rgba(229,83,83,.8); background: rgba(255,255,255,.35); line-height: 1.1; }
.canvas-field.selected { border: 2px solid #321fdb; background: rgba(255,255,255,.55); box-shadow: 0 0 0 1px rgba(50,31,219,.2); }
.canvas-field span { display: block; width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; padding: 1px 3px; }
.mapped-image { width: 100%; height: 100%; object-fit: contain; pointer-events: none; }
.resize-handle { position: absolute; right: -1px; bottom: -1px; width: 13px; height: 13px; padding: 0; border: 1px solid #fff; background: #321fdb; cursor: nwse-resize; }
</style>
